export const CART_GST_RATE = 0.18;
export const CART_SHIPPING_CHARGE = 350;
export const CART_PLATFORM_FEE = 199;
export const CART_FREE_SHIPPING_THRESHOLD = 23000;

export const formatCartInr = (amount) => {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
};

export function resolveCartItemSelection(item) {
  if (item?.isSelected != null) return Boolean(item.isSelected);
  if (item?.is_selected != null) return Number(item.is_selected) !== 0;
  return true;
}

export function resolveCartItemUnitPrice(item) {
  const direct = Number(item?.unitPrice ?? item?.unit_price ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return 0;
}

export function normalizeMarketplaceCartItem(item) {
  return {
    ...item,
    unitPrice: resolveCartItemUnitPrice(item),
    isSelected: resolveCartItemSelection(item),
  };
}

export function normalizeMarketplaceCart(cart) {
  if (!cart) return null;

  const suppliers = (cart.suppliers || []).map((supplier) => ({
    ...supplier,
    items: (supplier.items || []).map(normalizeMarketplaceCartItem),
  }));

  const items = suppliers.flatMap((supplier) => supplier.items || []);
  const summary = computeMarketplaceCartSummary(items);

  return {
    ...cart,
    suppliers,
    totalItems: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    summary,
  };
}

export function computeMarketplaceCartSummary(items = []) {
  const selected = items.filter((item) => resolveCartItemSelection(item));
  const subtotal = selected.reduce(
    (sum, item) => sum + resolveCartItemUnitPrice(item) * (Number(item.quantity) || 0),
    0,
  );
  const gst = subtotal * CART_GST_RATE;
  const shipping = selected.length > 0 ? CART_SHIPPING_CHARGE : 0;
  const platformFee = selected.length > 0 ? CART_PLATFORM_FEE : 0;
  const discount = 0;
  const grandTotal = subtotal + gst + shipping + platformFee - discount;
  const itemUnits = selected.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  return {
    itemUnits,
    lineCount: selected.length,
    subtotal,
    gst,
    shipping,
    platformFee,
    discount,
    grandTotal,
    freeShippingGap: Math.max(0, CART_FREE_SHIPPING_THRESHOLD - subtotal),
  };
}

export function flattenCartItems(cart) {
  return (cart?.suppliers || []).flatMap((supplier) =>
    (supplier.items || []).map((item) => ({
      ...normalizeMarketplaceCartItem(item),
      supplierId: supplier.id,
      supplierName: supplier.name,
    }))
  );
}

export function getCartItemCount(cart) {
  return Number(cart?.totalItems) || flattenCartItems(cart).reduce((sum, item) => sum + (item.quantity || 0), 0);
}

export function getSelectedIdsFromCart(cart) {
  return new Set(
    flattenCartItems(cart)
      .filter((item) => resolveCartItemSelection(item))
      .map((item) => item.id)
  );
}
